import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthGate from './AuthGate';
import AccountHeader from './AccountHeader';
import EventBonusOffer from './EventBonusOffer';
import '../static/css/SettingsPage.css';
import '../static/css/ManageSubscription.css';
import { trackScreen, SCREEN } from '../analytics';

const formatDate = (unixSeconds) => {
    if (!unixSeconds) return null;
    return new Date(unixSeconds * 1000).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
};

const formatAmount = (cents, currency) => {
    if (cents == null) return null;
    return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
    });
};

class ManageSubscription extends Component {
    constructor() {
        super();
        this.state = {
            loading: true,
            error: '',
            subscription: null,
            showCancelModal: false,
            isCanceling: false,
            isResuming: false,
            actionError: '',
            selectedPlan: 'monthly',
            justSubscribed: false,
            isStartingCheckout: false,
            isStartingUpdatePayment: false,
            successFlash: '',
        };
        this.fetchStatus = this.fetchStatus.bind(this);
        this.openCancelModal = this.openCancelModal.bind(this);
        this.closeCancelModal = this.closeCancelModal.bind(this);
        this.confirmCancel = this.confirmCancel.bind(this);
        this.handleResume = this.handleResume.bind(this);
        this.handleSelectPlan = this.handleSelectPlan.bind(this);
        this.handleSubscribe = this.handleSubscribe.bind(this);
        this.handleUpdatePayment = this.handleUpdatePayment.bind(this);
        this.finalizeUpdatePayment = this.finalizeUpdatePayment.bind(this);
    }

    async handleUpdatePayment() {
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ actionError: 'Your session has expired. Please sign in again.' });
            return;
        }
        this.setState({ isStartingUpdatePayment: true, actionError: '' });
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/web-subs-update-payment',
                {},
                { headers: { Authorization: 'Bearer ' + token } },
            );
            if (res.data && res.data.url) {
                window.location.href = res.data.url;
                return;
            }
            this.setState({
                isStartingUpdatePayment: false,
                actionError: 'Could not start payment update. Please try again.',
            });
        } catch (err) {
            this.setState({
                isStartingUpdatePayment: false,
                actionError: 'Could not start payment update. Please try again, or email hello@animates.app if it keeps happening.',
            });
        }
    }

    async finalizeUpdatePayment(sessionId) {
        const token = localStorage.getItem('token');
        if (!token) return;
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/web-subs-update-payment-finalize',
                { session_id: sessionId },
                { headers: { Authorization: 'Bearer ' + token } },
            );
            this.setState({
                subscription: res.data.data,
                successFlash: 'Payment method updated.',
            });
        } catch (err) {
            this.setState({
                actionError: 'Your new card was saved, but we could not attach it to the subscription. Please try again or email hello@animates.app.',
            });
        }
    }

    handleSelectPlan(plan) {
        if (this.state.isStartingCheckout) return;
        this.setState({ selectedPlan: plan });
    }

    async handleSubscribe() {
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ actionError: 'Your session has expired. Please sign in again.' });
            return;
        }
        this.setState({ isStartingCheckout: true, actionError: '' });
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/web-subs-checkout',
                { plan: this.state.selectedPlan },
                { headers: { Authorization: 'Bearer ' + token } },
            );
            if (res.data && res.data.url) {
                window.location.href = res.data.url;
                return;
            }
            this.setState({
                isStartingCheckout: false,
                actionError: 'Could not start checkout. Please try again.',
            });
        } catch (err) {
            this.setState({
                isStartingCheckout: false,
                actionError: 'Could not start checkout. Please try again, or email hello@animates.app if it keeps happening.',
            });
        }
    }

    openCancelModal() {
        if (this.state.isCanceling) return;
        this.setState({ showCancelModal: true, actionError: '' });
    }

    closeCancelModal() {
        if (this.state.isCanceling) return;
        this.setState({ showCancelModal: false });
    }

    async confirmCancel() {
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ actionError: 'Your session has expired. Please sign in again.', showCancelModal: false });
            return;
        }
        this.setState({ isCanceling: true, actionError: '' });
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/web-subs-cancel',
                {},
                { headers: { Authorization: 'Bearer ' + token } },
            );
            this.setState({
                isCanceling: false,
                showCancelModal: false,
                subscription: res.data.data,
            });
        } catch (err) {
            this.setState({
                isCanceling: false,
                showCancelModal: false,
                actionError: 'We could not cancel your subscription. Please try again, or email hello@animates.app if it keeps happening.',
            });
        }
    }

    async handleResume() {
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ actionError: 'Your session has expired. Please sign in again.' });
            return;
        }
        this.setState({ isResuming: true, actionError: '' });
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/web-subs-resume',
                {},
                { headers: { Authorization: 'Bearer ' + token } },
            );
            this.setState({
                isResuming: false,
                subscription: res.data.data,
            });
        } catch (err) {
            this.setState({
                isResuming: false,
                actionError: 'We could not resume your subscription. Please try again, or email hello@animates.app if it keeps happening.',
            });
        }
    }

    componentDidMount() {
        // screen_view, deduped module-scope in analytics.js — do NOT add a local guard.
        // No-ops entirely when signed out.
        trackScreen(SCREEN.MANAGE_SUBSCRIPTION);

        this.applyPreselectedPlan();
        this.handleCheckoutReturn();
        this.fetchStatus();
    }

    // When the user arrives from the signup funnel (/signup?plan=… → here), pre-select their chosen
    // plan so the Subscribe card is ready. This only sets the toggle — it never starts checkout.
    applyPreselectedPlan() {
        if (typeof window === 'undefined') return;
        const plan = new URLSearchParams(window.location.search).get('plan');
        if (plan === 'monthly' || plan === 'yearly') {
            this.setState({ selectedPlan: plan });
        }
    }

    handleCheckoutReturn() {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const checkout = params.get('checkout');
        const setup = params.get('setup');
        const sessionId = params.get('session_id');

        if (checkout) {
            if (checkout === 'success') {
                this.setState({ successFlash: 'Welcome to AniMates Premium!', justSubscribed: true });
                this.refreshSessionUser();
            } else if (checkout === 'cancelled') {
                this.setState({ actionError: 'Checkout was cancelled. No charge was made.' });
            }
        } else if (setup) {
            if (setup === 'success' && sessionId) {
                this.finalizeUpdatePayment(sessionId);
            } else if (setup === 'cancelled') {
                this.setState({ actionError: 'Payment update was cancelled. Your card on file is unchanged.' });
            }
        } else {
            return;
        }

        // Clean the query params so a refresh doesn't replay the toast / error / finalize call.
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, document.title, cleanUrl);
    }

    async refreshSessionUser() {
        const token = localStorage.getItem('token');
        if (!token) return;
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/web-session',
                {},
                { headers: { Authorization: 'Bearer ' + token } },
            );
            if (res.data && res.data.data && res.data.data.user) {
                localStorage.setItem('user', JSON.stringify(res.data.data.user));
            }
            if (res.data && res.data.data) {
                if (res.data.data.upcoming_event !== undefined) {
                    localStorage.setItem('upcoming_event', JSON.stringify(res.data.data.upcoming_event));
                }
            }
        } catch (err) {
            // Non-fatal — Home will refresh on next visit.
        }
    }

    async fetchStatus() {
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ loading: false, error: 'Your session has expired. Please sign in again.' });
            return;
        }
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/web-subs-status',
                {},
                { headers: { Authorization: 'Bearer ' + token } },
            );
            this.setState({ loading: false, subscription: res.data.data, error: '' });
        } catch (err) {
            this.setState({
                loading: false,
                error: 'We could not load your subscription right now. Please try again in a moment.',
            });
        }
    }

    renderLegacyBanner() {
        const sub = this.state.subscription;
        if (!sub || !sub.is_legacy_pricing) return null;
        return (
            <div className="manage-subscription-legacy-banner">
                <span className="manage-subscription-legacy-icon">💎</span>
                <div className="manage-subscription-legacy-text">
                    <span className="manage-subscription-legacy-title" style={{fontFamily: "'Chelsea Market', cursive"}}>
                        Legacy Pricing — locked in for life
                    </span>
                    <span className="manage-subscription-legacy-sub">
                        You're grandfathered at your sign-up rate. As long as your subscription stays active, this price never goes up.
                    </span>
                </div>
            </div>
        );
    }

    renderPaymentMethod() {
        const sub = this.state.subscription;
        const pm = sub && sub.payment_method;
        if (!pm) return null;
        return (
            <div className="manage-subscription-payment-method">
                <span className="manage-subscription-payment-label">Card on file:</span>
                <span className="manage-subscription-payment-value">
                    {pm.brand} •••• {pm.last4} • {String(pm.exp_month).padStart(2, '0')}/{String(pm.exp_year).slice(-2)}
                </span>
            </div>
        );
    }

    renderStatusPill(status, cancelAtPeriodEnd) {
        let label = status;
        let className = 'manage-subscription-pill-default';
        if (cancelAtPeriodEnd) {
            label = 'Canceling';
            className = 'manage-subscription-pill-canceling';
        } else if (status === 'active') {
            label = 'Active';
            className = 'manage-subscription-pill-active';
        } else if (status === 'trialing') {
            label = 'Free trial';
            className = 'manage-subscription-pill-trialing';
        } else if (status === 'past_due' || status === 'unpaid') {
            label = 'Payment failed';
            className = 'manage-subscription-pill-pastdue';
        }
        return <span className={`manage-subscription-pill ${className}`}>{label}</span>;
    }

    renderProviderLocked(source) {
        const isApple = source === 'APPLE';
        const providerLabel = isApple ? 'Apple' : 'Google';
        const accountLabel = isApple ? 'Apple ID' : 'Google account';
        const instructions = isApple
            ? <>Open <strong>Settings</strong> on your iPhone → tap your name at the top → <strong>Subscriptions</strong> → <strong>AniMates</strong>.</>
            : <>Open the <strong>Google Play Store</strong> app → tap your profile icon → <strong>Payments &amp; subscriptions</strong> → <strong>Subscriptions</strong> → <strong>AniMates</strong>.</>;
        return (
            <div className="manage-subscription-card manage-subscription-apple-locked">
                <p className="manage-subscription-locked-title" style={{fontFamily: "'Chelsea Market', cursive"}}>
                    Managed through {providerLabel}
                </p>
                <p className="manage-subscription-locked-message">
                    Your AniMates membership is billed through your <span className="manage-subscription-locked-provider">{accountLabel}</span>. To cancel or update your payment method, you'll need to do that on the {providerLabel} side — we can't change it from the web.
                </p>
                <p className="manage-subscription-locked-instructions">
                    {instructions}
                </p>
            </div>
        );
    }

    renderSubscribe() {
        const { selectedPlan, isStartingCheckout, actionError } = this.state;
        const features = [
            { icon: '⭐️', text: 'Premium Member Badge' },
            { icon: '⚡️', text: '+1,500 Power Level Boost (One-Time)' },
            { icon: '🔁', text: '+10 Bonus Power per Event Check-In' },
            { icon: '🎫', text: 'Early Access to New Events' },
            { icon: '🚀', text: 'Get Seen First at Events' },
            { icon: '🔒', text: 'Access to Premium-Only Events' },
            { icon: '🏆', text: 'Unlimited Hosting' },
            { icon: '✈️', text: 'Enhanced Travel Mode (Show Travel Route)' },
            { icon: '✏️', text: 'Faster & Discounted Display Name Changes' },
            { icon: '🔍', text: 'Advanced Event Filters' },
        ];
        return (
            <div className="manage-subscription-card">
                <p className="manage-subscription-headline" style={{fontFamily: "'Chelsea Market', cursive"}}>
                    Premium Feature Unlocks
                </p>
                <div className="manage-subscription-urgency">
                    <span className="manage-subscription-urgency-icon" role="img" aria-label="">⏰</span>
                    <span className="manage-subscription-urgency-text">
                        <strong>Limited time only.</strong> Subscribe now to lock in our lowest rate <strong>for life</strong>. Once we close legacy pricing, it's gone — and standard rates take over.
                    </span>
                </div>
                <ul className="manage-subscription-features">
                    {features.map((f, i) => (
                        <li key={i} className="manage-subscription-feature">
                            <span className="manage-subscription-feature-icon" role="img" aria-label="">{f.icon}</span>
                            <span className="manage-subscription-feature-text">{f.text}</span>
                        </li>
                    ))}
                </ul>
                <div className="manage-subscription-plan-picker">
                    <button
                        type="button"
                        className={`manage-subscription-plan-option${selectedPlan === 'monthly' ? ' manage-subscription-plan-option-selected' : ''}`}
                        onClick={() => this.handleSelectPlan('monthly')}
                        disabled={isStartingCheckout}
                        aria-pressed={selectedPlan === 'monthly'}
                    >
                        <span className="manage-subscription-plan-name">Monthly</span>
                        <span className="manage-subscription-plan-strike">$12.99/mo</span>
                        <span className="manage-subscription-plan-price">$9.99/mo</span>
                        <span className="manage-subscription-plan-badge">Save 38%</span>
                    </button>
                    <button
                        type="button"
                        className={`manage-subscription-plan-option${selectedPlan === 'yearly' ? ' manage-subscription-plan-option-selected' : ''}`}
                        onClick={() => this.handleSelectPlan('yearly')}
                        disabled={isStartingCheckout}
                        aria-pressed={selectedPlan === 'yearly'}
                    >
                        <span className="manage-subscription-plan-name">Yearly</span>
                        <span className="manage-subscription-plan-strike">$8.33/mo</span>
                        <span className="manage-subscription-plan-price">$6.42/mo</span>
                        <span className="manage-subscription-plan-badge">Save 51%</span>
                    </button>
                </div>
                {actionError && <p className="manage-subscription-action-error">{actionError}</p>}
                <button
                    type="button"
                    className="manage-subscription-cta"
                    onClick={this.handleSubscribe}
                    disabled={isStartingCheckout}
                >
                    {isStartingCheckout ? 'Starting checkout…' : `Subscribe ${selectedPlan === 'monthly' ? 'Monthly' : 'Yearly'}`}
                </button>
            </div>
        );
    }

    renderActiveOrTrialing() {
        const sub = this.state.subscription;
        const { isCanceling: isCancelInFlight, isResuming, isStartingUpdatePayment, actionError } = this.state;
        const isTrialing = sub.status === 'trialing';
        const isCanceling = !!sub.cancel_at_period_end;
        const periodEndDate = formatDate(sub.current_period_end);
        const trialEndDate = formatDate(sub.trial_end);
        const amountStr = formatAmount(sub.amount_cents, sub.currency);
        const planLabel = sub.plan === 'monthly' ? 'Monthly' : sub.plan === 'yearly' ? 'Yearly' : null;

        return (
            <div className="manage-subscription-card">
                <div className="manage-subscription-card-header">
                    <span className="manage-subscription-plan-label" style={{fontFamily: "'Chelsea Market', cursive"}}>
                        {planLabel ? `${planLabel} Plan` : 'Premium'}
                    </span>
                    {this.renderStatusPill(sub.status, isCanceling)}
                </div>

                {amountStr && (
                    <div className="manage-subscription-amount">
                        <span className="manage-subscription-amount-value">{amountStr}</span>
                        <span className="manage-subscription-amount-period">/{sub.plan === 'yearly' ? 'year' : 'month'}</span>
                    </div>
                )}

                {isCanceling && periodEndDate && (
                    <div className="manage-subscription-banner manage-subscription-banner-amber">
                        Your subscription will end on <strong>{periodEndDate}</strong>. You'll keep premium until then.
                    </div>
                )}

                {!isCanceling && isTrialing && trialEndDate && (
                    <div className="manage-subscription-banner manage-subscription-banner-blue">
                        Free trial — first charge on <strong>{trialEndDate}</strong>.
                    </div>
                )}

                {!isCanceling && !isTrialing && periodEndDate && (
                    <p className="manage-subscription-next-billing">
                        Next billing: <strong>{periodEndDate}</strong>
                    </p>
                )}

                {this.renderPaymentMethod()}

                {actionError && <p className="manage-subscription-action-error">{actionError}</p>}

                <div className="manage-subscription-actions">
                    {isCanceling ? (
                        <button
                            type="button"
                            className="manage-subscription-cta"
                            onClick={this.handleResume}
                            disabled={isResuming}
                        >
                            {isResuming ? 'Resuming…' : 'Resume subscription'}
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="manage-subscription-secondary"
                                onClick={this.handleUpdatePayment}
                                disabled={isStartingUpdatePayment}
                            >
                                {isStartingUpdatePayment ? 'Opening…' : 'Update payment method'}
                            </button>
                            <button
                                type="button"
                                className="manage-subscription-danger"
                                onClick={this.openCancelModal}
                                disabled={isCancelInFlight}
                            >
                                Cancel subscription
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    renderCancelModal() {
        const { isCanceling, subscription } = this.state;
        const showLegacyWarning = subscription && subscription.is_legacy_pricing;
        return (
            <div
                className="manage-subscription-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="manage-subscription-modal-title"
            >
                <div className="manage-subscription-modal-card">
                    <h3 id="manage-subscription-modal-title" className="manage-subscription-modal-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Are you sure?</h3>
                    <p className="manage-subscription-modal-message">
                        Your subscription will stay active until the end of your current billing period — then it ends and you'll lose premium.
                    </p>
                    {showLegacyWarning && (
                        <div className="manage-subscription-modal-warning">
                            <span className="manage-subscription-modal-warning-icon" role="img" aria-label="">💎</span>
                            <span className="manage-subscription-modal-warning-text">
                                <strong>You'll lose your legacy pricing — forever.</strong> You earned this locked-in rate by signing up early. If you cancel, it's gone — re-subscribing later will be at standard pricing, no exceptions.
                            </span>
                        </div>
                    )}
                    <div className="manage-subscription-modal-actions">
                        <button
                            type="button"
                            className="manage-subscription-modal-yes"
                            onClick={this.confirmCancel}
                            disabled={isCanceling}
                            autoFocus
                        >
                            {isCanceling ? 'Canceling…' : 'Yes, cancel'}
                        </button>
                        <button
                            type="button"
                            className="manage-subscription-modal-no"
                            onClick={this.closeCancelModal}
                            disabled={isCanceling}
                        >
                            Keep subscription
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderPastDue() {
        const sub = this.state.subscription;
        const { isStartingUpdatePayment, actionError } = this.state;
        return (
            <div className="manage-subscription-card">
                <div className="manage-subscription-card-header">
                    <span className="manage-subscription-plan-label" style={{fontFamily: "'Chelsea Market', cursive"}}>Premium</span>
                    {this.renderStatusPill(sub.status, false)}
                </div>
                <div className="manage-subscription-banner manage-subscription-banner-red">
                    Your last payment didn't go through. Update your card to keep premium active.
                </div>
                {this.renderPaymentMethod()}
                {actionError && <p className="manage-subscription-action-error">{actionError}</p>}
                <div className="manage-subscription-actions">
                    <button
                        type="button"
                        className="manage-subscription-cta"
                        onClick={this.handleUpdatePayment}
                        disabled={isStartingUpdatePayment}
                    >
                        {isStartingUpdatePayment ? 'Opening…' : 'Update payment method'}
                    </button>
                </div>
            </div>
        );
    }

    renderBody() {
        const { loading, error, subscription } = this.state;

        if (loading) {
            return (
                <div className="manage-subscription-loading" role="status" aria-live="polite">
                    <div className="loading-spinner"></div>
                </div>
            );
        }

        if (error) {
            return <p className="manage-subscription-error">{error}</p>;
        }

        if (!subscription) {
            return <p className="manage-subscription-error">Subscription details unavailable.</p>;
        }

        if (subscription.source === 'APPLE' || subscription.source === 'GOOGLE') return this.renderProviderLocked(subscription.source);

        if (subscription.status === 'past_due' || subscription.status === 'unpaid') {
            return (
                <>
                    {this.renderLegacyBanner()}
                    {this.renderPastDue()}
                </>
            );
        }

        if (subscription.source === 'STRIPE' && (subscription.status === 'active' || subscription.status === 'trialing')) {
            return (
                <>
                    {this.renderLegacyBanner()}
                    {this.renderActiveOrTrialing()}
                </>
            );
        }

        return this.renderSubscribe();
    }

    titleForState() {
        const sub = this.state.subscription;
        if (!sub) return 'Manage Subscription';
        if (sub.source === 'APPLE' || sub.source === 'GOOGLE') return 'Manage Subscription';
        if (sub.source === 'STRIPE' && (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due')) return 'Manage Subscription';
        return 'Subscribe to AniMates';
    }

    render() {
        return (
            <AuthGate>
                <div className="settings-page">
                    <AccountHeader />
                    <div className="settings-page-content manage-subscription-content">
                        <Link to="/home" className="settings-back-link">Back</Link>
                        <h2 className="settings-page-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}>
                            <u>{this.titleForState()}</u>
                        </h2>
                        {this.state.successFlash && (
                            <div className="manage-subscription-flash" role="status" aria-live="polite">
                                {this.state.successFlash}
                            </div>
                        )}
                        {this.renderBody()}
                        {this.state.justSubscribed && (
                            <>
                                <EventBonusOffer />
                                <Link to="/create-event-opportunity" className="no-dec"><button className="manage-subscription-cta" style={{width: "100%", maxWidth: "420px", margin: "12px auto 0 auto", display: "block"}}>Create an Event</button></Link>
                            </>
                        )}
                    </div>
                    {this.state.showCancelModal && this.renderCancelModal()}
                </div>
            </AuthGate>
        );
    }
}

export default ManageSubscription;
